import React from "react";
import {Box, IconButton, InputAdornment, Link, Stack, TextField, Tooltip, Typography, Divider, Alert} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionIcon from "@mui/icons-material/Description";
import {Popup} from "semantic-ui-react";

import {SubtitleInline} from "components/shared/Typography";
import FileUploadButton from "components/shared/FileUploadButton";
import {useParams} from "react-router-dom";
import FileUploadDragAndDrop from "components/shared/FileUploadDragAndDrop";

export default function CertificateFileSection({
  vendor,
  acord25FileName, // name of the acord 25 file
  certificateFiles, // list of files from S3
  handleFileUpload,
  handleFileDelete,
  fileSelected,
  setFileSelected,
  showVendorLink,
  disableUpload = false,
  saving = false,
  vendorLabel,
  children,
}) {
  const {clientId, certificateId} = useParams()
  const isCreate = certificateId === 'create';
  const acord25File = certificateFiles?.find(file => (file.name === acord25FileName || file.isAcord25) && !file.isDeleted)
  const fileLineItemProps = {fileSelected, setFileSelected}
  return (
    <>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '2px solid', borderColor: 'primary.200' }}>
        <Stack display="flex" justifyContent="space-between" flexDirection="row" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeIcon color="primary" />
              <SubtitleInline sx={{ mb: 0 }}>
                ACORD 25 CERTIFICATE FILE (AI-Powered Extraction)
              </SubtitleInline>
            </Stack>
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              Upload your ACORD 25 certificate here for automatic AI-powered data extraction.
              This file will be scanned and all fields will be automatically populated.
            </Alert>
            {
              acord25File
              ? <FileLineItem file={acord25File} {...fileLineItemProps} />
              : <Typography variant={"subtitle2"} color={"text.secondary"} ml={2}>No ACORD 25 file uploaded yet.</Typography>
            }
          </Box>
          <FileUploadDragAndDrop
            flexGrow={1}
            buttonText= {acord25File ? "REPLACE ACORD 25" : "UPLOAD ACORD 25 PDF"}
            buttonProps={{
              startIcon: <AddIcon />,
              disabled: disableUpload || saving,
              variant: "contained",
              color: "primary"
            }}
            onChange={event => handleFileUpload(event, acord25FileName || true)}
            />
        </Stack>
      </Box>
      <Divider sx={{mt: 2, mb: 2}}/>

        {children}
      
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
        <Stack display="flex" justifyContent="space-between" flexDirection="row" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon color="action" />
              <SubtitleInline sx={{ mb: 0 }}>
                OPTIONAL SUPPLEMENTAL DOCUMENTS
              </SubtitleInline>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>
              Additional supporting documents (endorsements, declarations, etc.). These files will NOT be scanned.
            </Typography>
            {
              certificateFiles?.map((uploadedFile, index) => (
                  !uploadedFile.isAcord25 &&
                  <FileLineItem file={uploadedFile} {...fileLineItemProps} handleFileDelete={handleFileDelete} key={"supplement"+ index}/>
                )
              )
            }
          </Box>
          <FileUploadDragAndDrop
            flexGrow={1}
            buttonText="ADD SUPPLEMENTAL FILE"
            buttonProps={{
              startIcon: <AddIcon />,
              disabled: disableUpload || saving,
              variant: "outlined"
            }}
            sx={{border: 'none'}}
            onChange={handleFileUpload}
          />
        </Stack>
      </Box>
      <Divider sx={{mt: 2, mb: 1}}/>    
    </>
  )
}

const FileLineItem = ({file, handleFileDelete, fileSelected, setFileSelected}) => (
  <Stack direction="row" alignItems={"center"}>
    {
      handleFileDelete &&
      <IconButton
        onClick={()=>handleFileDelete(file.name)}
        sx={{mr: 1}}
      >
        {
          file.isDeleted
            ? <RestorePageOutlinedIcon />
            : <DeleteForeverIcon />
        }
      </IconButton>
    }

    <Link
      component={"button"}
      onClick={() => setFileSelected({ name: file?.name })}
      color={
        (fileSelected?.name === file.name && !file.isDeleted)
          ? "primary"
          : "inherit"
      }
      sx={{textDecoration: file.isDeleted ? "line-through" : "underline", ml: (handleFileDelete ? 0 : 2)}}
    >
      {file.name}
    </Link>
    {
      file.isNew &&
      <Box sx={{ml: 1}}>
        <Tooltip title={"Your files are not saved until you click Save"} >
          <Typography variant="caption"> - new</Typography>
        </Tooltip>
      </Box>
    }
  </Stack>
)
